import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  ActivityIcon,
  AlertTriangle,
  Banknote,
  Boxes,
  ClipboardList,
  Flag,
  PackageOpen,
  RotateCcw,
  Sparkles,
  UserPlus,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/data/EmptyState';
import { DateTime } from '@/components/data/DateTime';
import { Badge } from '@/components/ui/Badge';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';
import {
  useCommerceDashboard,
  usePlatformDashboard,
} from '@/queries/dashboard';
import { useAuditEvents } from '@/queries/audit';
import { useApiError } from '@/lib/useApiError';
import { formatCents } from '@/lib/money';
import { formatNumber } from '@/lib/dates';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_authed/dashboard')({
  component: DashboardScreen,
});

interface CounterCardProps {
  to: string;
  search?: Record<string, string>;
  Icon: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'neutral' | 'warn' | 'danger' | 'success';
  loading?: boolean;
}

const TONE_CLASS = {
  neutral: 'border-[color:var(--color-border)]',
  warn: 'border-[color:var(--color-warn)] bg-[color:var(--color-warn-soft)]/40',
  danger: 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)]/40',
  success: 'border-[color:var(--color-success)] bg-[color:var(--color-success-soft)]/40',
} as const;

function CounterCard({
  to,
  search,
  Icon,
  label,
  value,
  hint,
  tone = 'neutral',
  loading,
}: CounterCardProps) {
  // TanStack Router's typed Link with exactOptionalPropertyTypes refuses an
  // explicit `undefined` for `search`. Spread the prop only when present.
  const linkProps = search ? { search } : {};
  return (
    <Link
      to={to}
      {...linkProps}
      className={cn(
        'group block rounded-[var(--radius-lg)] border bg-[color:var(--color-surface)] p-5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2',
        TONE_CLASS[tone]
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-[color:var(--color-fg-muted)]">{label}</span>
        <Icon aria-hidden className="size-5 text-[color:var(--color-fg-muted)] transition-colors group-hover:text-[color:var(--color-accent)]" />
      </div>
      <div className="mt-3 text-2xl font-semibold tabular-nums text-[color:var(--color-fg)]" dir="ltr">
        {loading ? (
          <span className="inline-block h-7 w-20 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
        ) : (
          value
        )}
      </div>
      {hint ? <div className="mt-1 text-xs text-[color:var(--color-fg-muted)]">{hint}</div> : null}
    </Link>
  );
}

function DashboardScreen() {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const locale = useUiStore((s) => s.locale);
  const userId = useAuthStore((s) => s.userId);
  const resolveError = useApiError();

  const platform = usePlatformDashboard();
  const commerce = useCommerceDashboard();
  const audit = useAuditEvents();

  const myActivity = React.useMemo(() => {
    if (!audit.data || !userId) return [];
    return audit.data.filter((e) => e.actor_id === userId).slice(0, 20);
  }, [audit.data, userId]);

  const platformError = platform.error ? resolveError(platform.error) : null;
  const commerceError = commerce.error ? resolveError(commerce.error) : null;

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>

      <section aria-labelledby="dash-platform-h" className="space-y-3">
        <h2 id="dash-platform-h" className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          {t('platform')}
        </h2>
        {platformError ? (
          <EmptyState variant="error" title={tc('errors.title')} body={platformError.message} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <CounterCard
              to="/users"
              Icon={ActivityIcon}
              label={t('counters.activeUsers')}
              hint={t('counters.activeUsersHint')}
              loading={platform.isLoading}
              value={platform.data ? formatNumber(platform.data.active_users, locale) : '—'}
            />
            <CounterCard
              to="/users"
              Icon={UserPlus}
              label={t('counters.signups24h')}
              loading={platform.isLoading}
              value={platform.data ? formatNumber(platform.data.signups_last_24h, locale) : '—'}
            />
            <CounterCard
              to="/moderation/queue"
              search={{ state: 'open' }}
              Icon={Flag}
              label={t('counters.openReports')}
              loading={platform.isLoading}
              tone={platform.data && platform.data.open_reports > 0 ? 'warn' : 'neutral'}
              value={platform.data ? formatNumber(platform.data.open_reports, locale) : '—'}
            />
            <CounterCard
              to="/moderation/queue"
              search={{ state: 'open', breached: '1' }}
              Icon={AlertTriangle}
              label={t('counters.slaBreaches')}
              loading={platform.isLoading}
              tone={platform.data && platform.data.sla_breaches > 0 ? 'danger' : 'success'}
              value={platform.data ? formatNumber(platform.data.sla_breaches, locale) : '—'}
            />
          </div>
        )}
      </section>

      <section aria-labelledby="dash-commerce-h" className="space-y-3">
        <h2 id="dash-commerce-h" className="text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          {t('commerce')}
        </h2>
        {commerceError ? (
          <EmptyState variant="error" title={tc('errors.title')} body={commerceError.message} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <CounterCard
              to="/commerce/orders"
              Icon={Banknote}
              label={t('counters.gmvToday')}
              loading={commerce.isLoading}
              hint={t('counters.gmvTodayHint')}
              value={
                commerce.data
                  ? formatCents(
                      commerce.data.gmv_today_cents,
                      locale,
                      commerce.data.gmv_currency ?? 'EGP'
                    )
                  : '—'
              }
            />
            <CounterCard
              to="/commerce/orders"
              Icon={PackageOpen}
              label={t('counters.openOrders')}
              loading={commerce.isLoading}
              value={commerce.data ? formatNumber(commerce.data.open_orders, locale) : '—'}
            />
            <CounterCard
              to="/commerce/returns"
              Icon={RotateCcw}
              label={t('counters.pendingReturns')}
              loading={commerce.isLoading}
              value={commerce.data ? formatNumber(commerce.data.pending_returns, locale) : '—'}
            />
            <CounterCard
              to="/commerce/payouts"
              Icon={Boxes}
              label={t('counters.payoutsDue')}
              loading={commerce.isLoading}
              value={commerce.data ? formatNumber(commerce.data.payouts_due, locale) : '—'}
            />
          </div>
        )}
      </section>

      <section aria-labelledby="dash-activity-h" className="space-y-3">
        <h2
          id="dash-activity-h"
          className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]"
        >
          <ClipboardList className="size-4" aria-hidden />
          {t('myActivity')}
        </h2>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('myActivityTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {audit.isLoading ? (
              <ul className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <li key={i} aria-hidden className="h-6 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
                ))}
              </ul>
            ) : myActivity.length === 0 ? (
              <EmptyState
                title={t('myActivityEmptyTitle')}
                body={t('myActivityEmptyBody')}
                icon={<Sparkles className="size-6" />}
              />
            ) : (
              <ul className="divide-y divide-[color:var(--color-border)]">
                {myActivity.map((event) => (
                  <li key={event.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="flex flex-1 items-center gap-3">
                      <Badge tone={event.action.startsWith('admin.pii_reveal') ? 'warn' : 'accent'}>
                        {event.action}
                      </Badge>
                      <span className="text-sm text-[color:var(--color-fg-muted)]" dir="ltr">
                        {event.subject_kind ?? '—'}
                        {event.subject_id ? `:${event.subject_id.slice(0, 8)}…` : ''}
                      </span>
                    </div>
                    <DateTime value={event.created_at} className="text-xs text-[color:var(--color-fg-muted)]" />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
